// (top remains unchanged)
import React, { useState } from 'react';
import './ReportsPage.css';
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { FaCalendarAlt, FaDownload, FaChartPie } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { reportOptions } from './reportOptions';
import { getClinicId } from '../../utils/config';
import FormatUrl from '../../utils/UrlFormatter';

const ReportsPage = ({ user }) => {
  const { t } = useTranslation();
  const clinicId = getClinicId(user);

  const [selectedReport, setSelectedReport] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [lastDownloaded, setLastDownloaded] = useState(null);

  const validateDates = () => {
    if (!startDate || !endDate) {
      setDateError('');
      return false;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      setDateError(t('reports.errors.start_after_end'));
      return false;
    }

    setDateError('');
    return true;
  };

  const handleDownload = async () => {
    if (!selectedReport) {
      toast.error(t('reports.errors.incomplete_fields'));
      return;
    }

    const key = selectedReport.key;

    if (key === 'practitioner_hours' && (!startDate || !endDate)) {
      toast.error(t('reports.errors.incomplete_fields'));
      return;
    }

    if (key === 'practitioner_hours' && !validateDates()) {
      toast.error(t('reports.errors.invalid_dates'));
      return;
    }

    let url = '';
    let filename = '';

    if (key === 'practitioner_hours') {
      url = FormatUrl(
        `/reports/appointment-pc-report/?clinic_id=${clinicId}&start_date=${startDate}&end_date=${endDate}`
      );
      filename = `practitioner_work_hours_${startDate}_to_${endDate}.csv`;
    } else if (key === 'new_patients_without_appointments') {
      url = FormatUrl(`/reports/patients-without-appointments/?clinic_id=${clinicId}`);
      filename = `new_patients_without_appointments.csv`;
    } else {
      toast.error('Report not supported yet.');
      return;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch report');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setLastDownloaded({
        key,
        name: t(`reports.options.${key}.name`),
        filename,
        startDate,
        endDate
      });

      setTimeout(() => {
        setLastDownloaded(null);
      }, 8000);

      toast.success(t('reports.success.download_started', { name: t(`reports.options.${key}.name`) }));
    } catch (error) {
      toast.error(t('reports.errors.fetch_failed'));
      console.error(error);
    }
  };

  const isDownloadDisabled =
    !selectedReport ||
    (selectedReport.key === 'practitioner_hours' && (!startDate || !endDate));

  return (
    <div className="reports_wrapper">
      <div className="reports_dashboard">
        <h1 className="reports_heading">
          <span className="icon-spin"><FaChartPie /></span> {t('reports.title')}
        </h1>
        <p className="reports_subtext">{t('reports.subtitle')}</p>

        <div className="reports_layout">
          <aside className="reports_sidebar">
            <h3>{t('reports.sidebar_title')}</h3>
            <ul className="report_list_scrollable">
              {reportOptions.map((report) => (
                <li
                  key={report.key}
                  className={`report_item ${selectedReport?.key === report.key ? 'active' : ''}`}
                  onClick={() => setSelectedReport(report)}
                >
                  <span className="report_icon">{report.icon}</span>
                  <span>{t(`reports.options.${report.key}.name`)}</span>
                </li>
              ))}
            </ul>
          </aside>

          <section className="reports_content">
            {selectedReport ? (
              <div className="report_card">
                <h2>{t(`reports.options.${selectedReport.key}.name`)}</h2>
                <p className="report_description">
                  {t(`reports.options.${selectedReport.key}.description`)}
                </p>

                {selectedReport.key === 'practitioner_hours' && (
                  <div className="date_filter_row">
                    <div className="date_input_block">
                      <label>{t('reports.start_date')}</label>
                      <div className="input_with_icon">
                        <FaCalendarAlt />
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          max={endDate || undefined}
                        />
                      </div>
                    </div>

                    <div className="date_input_block">
                      <label>{t('reports.end_date')}</label>
                      <div className="input_with_icon">
                        <FaCalendarAlt />
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          min={startDate || undefined}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <button
                  className="download_btn"
                  onClick={handleDownload}
                  disabled={isDownloadDisabled}
                  style={{
                    opacity: isDownloadDisabled ? 0.6 : 1,
                    cursor: isDownloadDisabled ? 'not-allowed' : 'pointer',
                    marginTop: selectedReport.key === 'practitioner_hours' ? '1.25rem' : '0.5rem'
                  }}
                >
                  <FaDownload /> {t('reports.download_btn')}
                </button>

                {lastDownloaded && lastDownloaded.key === selectedReport.key && (
                  <div className="download-summary_v1">
                    <button
                      className="summary-close-btn_v1"
                      onClick={() => setLastDownloaded(null)}
                    >
                      ✖️
                    </button>
                    <div>
                      <strong>{t('reports.download_summary.title')}</strong><br />
                      {t('reports.download_summary.report')}: {lastDownloaded.name}<br />
                      {lastDownloaded.startDate && lastDownloaded.endDate && (
                        <>
                          {t('reports.download_summary.range')}: {lastDownloaded.startDate} → {lastDownloaded.endDate}<br />
                        </>
                      )}
                      {t('reports.download_summary.file')}: <code>{lastDownloaded.filename}</code>
                    </div>
                  </div>
                )}

                {dateError && (
                  <p style={{ color: 'var(--red-light)', marginTop: '0.75rem' }}>
                    ⚠️ {dateError}
                  </p>
                )}
              </div>
            ) : (
              <p className="no_report_text">{t('reports.no_selection')}</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
